import React, { useState } from 'react';
import { Container, Header, Table, Box, Link } from '@amzn/awsui-components-react-v3';
import { StuckRow } from './types';
import { STUCK_TICKET_THRESHOLD } from './constants';
import { isPlatformOwned, bucketUrl } from './stuckLayout';

interface Props {
    rows: StuckRow[];
}

interface PlatformRow {
    plugin: string;
    region: string;
    workflow: string;
    state: string;
    stuck: number;
}

// PCES ClassifyError-family states that are NOT exact op-segments; SAM's plugin rule can't match them.
const PlatformStatesTable: React.FC<Props> = ({ rows }) => {
    const g = new Map<string, PlatformRow>();
    rows.filter(isPlatformOwned).forEach((r) => {
        const k = `${r.plugin}|${r.region}|${r.workflow}|${r.state}`;
        const o = g.get(k) || { plugin: r.plugin, region: r.region, workflow: r.workflow, state: r.state, stuck: 0 };
        o.stuck += r.stuck;
        g.set(k, o);
    });
    const all = Array.from(g.values()).sort((a, b) => b.stuck - a.stuck);
    const over = all.filter((x) => x.stuck > STUCK_TICKET_THRESHOLD);
    const stuckTot = over.reduce((s, x) => s + x.stuck, 0);
    const plugins = new Set(over.map((x) => x.plugin)).size;
    const under = all.filter((x) => x.stuck <= STUCK_TICKET_THRESHOLD);
    const underTot = under.reduce((s, x) => s + x.stuck, 0);
    const [showOthers, setShowOthers] = useState(false);
    const items = showOthers ? [...over, ...under] : over;

    return (
        <Container
            header={
                <Header
                    variant="h2"
                    counter={`(${over.length})`}
                    description={`Suffixed / other ClassifyError states SAM can't match by plugin op → owned by the Refunds team. ${over.length} bucket(s) > ${STUCK_TICKET_THRESHOLD} across ${plugins} plugin(s), ${stuckTot.toLocaleString()} stuck.`}
                >
                    🧩 Platform-owned states
                </Header>
            }
        >
            <Table
                variant="borderless"
                items={items}
                footer={under.length > 0 ? (
                    <Box textAlign="center">
                        <Link onFollow={(e) => { e.preventDefault(); setShowOthers((v) => !v); }}>
                            {showOthers
                                ? `▾ Hide ${under.length} others ≤ ${STUCK_TICKET_THRESHOLD}`
                                : `▸ Show ${under.length} others ≤ ${STUCK_TICKET_THRESHOLD} (${underTot.toLocaleString()} stuck) — with bucket links`}
                        </Link>
                    </Box>
                ) : undefined}
                empty={<Box textAlign="center" color="inherit">No platform-owned buckets over threshold.</Box>}
                columnDefinitions={[
                    { id: 'plugin', header: 'Plugin', cell: (x) => x.plugin },
                    { id: 'region', header: 'Region', cell: (x) => x.region },
                    { id: 'state', header: 'State', cell: (x) => <Box color="text-body-secondary"><span title={x.state}>{x.state}</span></Box> },
                    { id: 'stuck', header: 'Stuck', cell: (x) => x.stuck.toLocaleString() },
                    { id: 'bucket', header: 'Bucket', cell: (x) => <Link external href={bucketUrl(x.region, x.plugin, x.workflow, x.state)}>bucket</Link> },
                    { id: 'owner', header: 'Owner', cell: () => <Box color="text-body-secondary">Refunds team</Box> },
                ]}
            />
        </Container>
    );
};

export default PlatformStatesTable;
